import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { requiredRule, validEmailRule } from "@/utilities/form"
import { useScopedNavigate } from "@/utilities/hooks/useScopedRouting"

import styles from "./index.module.css"

export const Route = createFileRoute("/auth/sign-in/")({
  component: RouteComponent,
})

interface SignInFormSchema {
  email: string
  password: string
}

function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useSignInMutation({
  form,
}: {
  form: ReturnType<typeof useForm<SignInFormSchema>>
}) {
  const router = useRouter()
  const navigate = Route.useNavigate()
  const scopedNavigate = useScopedNavigate()
  const search = Route.useSearch()
  const { t } = useTranslation(["common", "errors", "gated"])

  return useMutation({
    mutationFn: async (data: SignInFormSchema) => {
      return api.auth.signIn({
        username: data.email,
        password: data.password,
      })
    },
    onSuccess: async (result, variables) => {
      if (result.isSignedIn) {
        // Normal redirect flow when no guest data
        if (search.redirect) {
          router.navigate({ to: search.redirect })
        } else {
          scopedNavigate({ to: "/" })
        }
      } else if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        navigate({
          to: "/auth/verify",
          search: {
            ...search,
            email: variables.email,
            context: "sign-in",
          },
        })
      } else {
        form.setError("root", {
          message: t("auth.additionalAuthStep", { ns: "errors" }),
        })
      }
    },
    onError: (err: any, variables) => {
      logMutationError(err, {
        flow: "sign-in",
        email: variables.email,
        qrLookupID: search.qrLookupID,
        additionalData: { redirect: search.redirect },
      })
      handleAuthError(err, form, t)
    },
  })
}

function RouteComponent() {
  const search = Route.useSearch()
  const { t } = useTranslation(["common", "errors"])

  const form = useForm<SignInFormSchema>({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onBlur",
  })

  const signInMutation = useSignInMutation({ form })

  const onSubmit = (data: SignInFormSchema) => {
    form.clearErrors("root")
    signInMutation.mutate(data)
  }

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.signIn.heading", { ns: "common" })}
      </Heading>

      <Text level={2} style={{ marginTop: "var(--space-2)" }}>
        {t("auth.signIn.description", { ns: "common" })}
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset
          style={{ width: "100%", minWidth: 0 }}
          disabled={signInMutation.isPending}
        >
          <Controller<SignInFormSchema>
            name="email"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              pattern: validEmailRule(t("common.validEmail", { ns: "errors" })),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.signIn.email", { ns: "common" })}</Label>
                <TextField.Input
                  innerRef={ref}
                  type="email"
                  placeholder="alex.smith@example.com"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<SignInFormSchema>
            name="password"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <div className={styles.passwordLabelRow}>
                  <Label>{t("auth.signIn.password", { ns: "common" })}</Label>
                  <LinkButton
                    buttonType="primary"
                    presentation="link"
                    to="/auth/forgot-password"
                    search={search}
                    style={{
                      fontSize: "var(--font-size-2)",
                      fontWeight: "400",
                      textDecorationLine: "underline",
                    }}
                  >
                    {t("auth.signIn.troubleSigningIn", { ns: "common" })}
                  </LinkButton>
                </div>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            buttonType="primary"
            type="submit"
            isPending={signInMutation.isPending}
          >
            {t("auth.signIn.submitButton", { ns: "common" })}
          </Button>
        </fieldset>
      </form>

      {/* Create Account Link */}
      <Text level={2} className="center">
        {t("auth.signIn.noAccount", { ns: "common" })}{" "}
        <LinkButton
          buttonType="primary"
          presentation="link"
          to="/auth/create-account"
          search={search}
          style={{ textDecorationLine: "underline" }}
        >
          {t("auth.signIn.signUpLink", { ns: "common" })}
        </LinkButton>
        .
      </Text>
    </>
  )
}
